import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  DialogActions,
  Stack,
  Collapse,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import {
  CalendarPicker,
  LocalizationProvider,
  MobileDatePicker,
} from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import { useAppSelector } from '../../../../hooks';
import { getCoursesState } from '../../../../store/V2/CoursesSlice';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import { ISelectedOption } from '../../../../constants';
import {
  getShortName,
  isCourseTutorEligibleForClass,
} from '../../../../helper';
import {
  getTutorAvailabilitySlotsForProfile,
  getUserAvailabilityState,
  resetUserAvailabilitySlotOptionsForTrial,
} from '../../../../store/V2/UserAvailabilitySlice';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import {
  bookCourseMeeting,
  TBookCourseMeeting,
} from '../../../../store/V2/SectionSlice';

interface BookClassModalProps {
  open: boolean;
  onClose: () => void;
}

type TSlotOption = {
  start: string;
  end: string;
  user: string;
};

type TState = {
  selectedDate: Moment | null;
  selectedTutor: ISelectedOption | null;
  tutorOptions: ISelectedOption[];
  selectedSlot: TSlotOption | null;
};

const initialState: TState = {
  selectedDate: moment().add(1, 'day'),
  selectedTutor: null,
  tutorOptions: [],
  selectedSlot: null,
};

const SlotCard = (props: {
  isSelected?: boolean;
  slot: TSlotOption;
  onClick: () => void;
}) => {
  const { isSelected, slot, onClick } = props;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #01A79F',
        borderRadius: '.375rem',
        backgroundColor: isSelected ? '#01A79F' : undefined,
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ p: '.5rem 1rem' }}>
          <Typography
            fontSize="14px"
            fontWeight={500}
            color={isSelected ? '#ffffff' : '#01A79F'}
            textAlign="center"
          >
            {moment(slot.start).format('hh:mm A')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const BookClassModal: React.FC<BookClassModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const userAvailabilityState = useAppSelector(getUserAvailabilityState);

  const { userCourse: courseData } = coursesState;
  const { userAvailabilitySlotOptionsForTrial } = userAvailabilityState;

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isFreeSlotsLoading, setIsFreeSlotsLoading] = useState(false);
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  let isTutorEligible = false;

  const freeSlots: TSlotOption[] = [];

  if (
    state.selectedTutor &&
    state.selectedDate &&
    courseData &&
    courseData.tutors &&
    courseData.tutors.length
  ) {
    const matchingCourseTutor = courseData.tutors.find((item) => {
      const _tutor = item.tutor;

      if (_tutor && typeof _tutor === 'object' && state.selectedTutor) {
        return _tutor._id === state.selectedTutor.value;
      }

      return false;
    });

    if (matchingCourseTutor) {
      isTutorEligible = isCourseTutorEligibleForClass(
        matchingCourseTutor,
        state.selectedDate.toISOString(),
        60
      );
    }
  }

  if (userAvailabilitySlotOptionsForTrial && isTutorEligible) {
    userAvailabilitySlotOptionsForTrial.forEach((slot) => {
      if (
        state.selectedDate &&
        state.selectedTutor &&
        slot.user &&
        slot.user === state.selectedTutor.value
      ) {
        const slotDateString = moment(slot.start).format('YYYY-MM-DD');
        const selectedDateString = state.selectedDate.format('YYYY-MM-DD');

        if (slotDateString === selectedDateString) {
          freeSlots.push({
            start: slot.start,
            end: slot.end,
            user: slot.user,
          });
        }
      }
    });
  }

  useEffect(() => {
    initiateModal();

    return () => {
      dispatch(resetUserAvailabilitySlotOptionsForTrial());
    };
  }, []);

  const initiateModal = async () => {
    if (courseData) {
      const newState = { ...state };
      const tutors = courseData.tutors || [];
      const newTutorOptions: ISelectedOption[] = [];

      tutors.forEach((tutor) => {
        const tutorData = tutor.tutor;

        if (tutorData && typeof tutorData === 'object') {
          if (tutorData.firstname && tutorData.lastname) {
            newTutorOptions.push({
              label: getShortName(
                tutorData.firstname,
                tutorData.lastname,
                true
              ),
              value: tutor.tutor._id,
            });
          }
        }
      });

      newState.tutorOptions = newTutorOptions;

      if (newTutorOptions.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        newState.selectedTutor = newTutorOptions[0];
      }

      setState({ ...newState });

      if (newState.selectedTutor && newState.selectedDate) {
        setIsFreeSlotsLoading(true);

        await dispatch(
          getTutorAvailabilitySlotsForProfile({
            tutor: newState.selectedTutor.value,
            date: newState.selectedDate.clone().startOf('day').toISOString(),
          })
        );

        setIsFreeSlotsLoading(false);
      }
    }
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({ ...prevState, ...attributes }));
  };

  const handleTutorChange = async (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption | null
  ) => {
    setIsFreeSlotsLoading(true);
    updateStateAttributes({ selectedTutor: value, selectedSlot: null });

    dispatch(resetUserAvailabilitySlotOptionsForTrial());

    if (value && state.selectedDate) {
      await dispatch(
        getTutorAvailabilitySlotsForProfile({
          tutor: value.value,
          date: state.selectedDate.clone().startOf('day').toISOString(),
        })
      );
    }

    setIsFreeSlotsLoading(false);
  };

  const handleDateChange = async (date: Moment | null) => {
    setIsFreeSlotsLoading(true);
    updateStateAttributes({ selectedDate: date, selectedSlot: null });

    dispatch(resetUserAvailabilitySlotOptionsForTrial());

    if (date && state.selectedTutor) {
      await dispatch(
        getTutorAvailabilitySlotsForProfile({
          tutor: state.selectedTutor.value,
          date: date.clone().startOf('day').toISOString(),
        })
      );
    }

    setIsFreeSlotsLoading(false);
  };

  const handleSubmit = async () => {
    setIsBookingLoading(true);

    if (
      state.selectedSlot &&
      state.selectedTutor &&
      state.selectedDate &&
      courseData
    ) {
      const apiData: TBookCourseMeeting = {
        start: state.selectedSlot.start,
        tutorId: state.selectedTutor.value,
        courseId: courseData._id,
      };

      const res: any = await dispatch(bookCourseMeeting(apiData));

      if (res.payload) {
        onClose();
      }
    }

    setIsBookingLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={isDesktop ? 'md' : 'sm'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Book A Lesson
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div role="presentation">
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container columnGap="30px" rowGap="20px">
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  sx={{ maxWidth: isDesktop ? '300px' : undefined }}
                >
                  <div style={{ minHeight: '6px' }} />
                  <Autocomplete
                    size="small"
                    value={state.selectedTutor}
                    options={state.tutorOptions}
                    onChange={handleTutorChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    getOptionKey={(option) => option.value}
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Tutor *"
                        placeholder="Tutor"
                        helperText={
                          !isTutorEligible ? (
                            <Typography variant="caption" color="error.main">
                              Tutor is not available for this date.
                            </Typography>
                          ) : undefined
                        }
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {isDesktop ? (
                <Grid item xs={12} md="auto">
                  <FormControl fullWidth sx={{ width: { lg: '300px' } }}>
                    <CalendarPicker
                      disabled={!state.selectedTutor}
                      views={['day']}
                      minDate={moment()}
                      date={state.selectedDate}
                      onChange={handleDateChange}
                    />
                  </FormControl>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MobileDatePicker
                      disabled={!state.selectedTutor}
                      value={state.selectedDate}
                      minDate={moment()}
                      onChange={handleDateChange}
                      label="Date *"
                      inputFormat="YYYY MMM DD"
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...defaultTextFieldProps}
                          size="small"
                          placeholder="Date"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <InputAdornment position="end">
                                <DateRangeIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md>
                <Stack gap="10px">
                  <div>
                    <Typography fontWeight={600}>Select a slot</Typography>
                  </div>
                  {isFreeSlotsLoading ? (
                    <Collapse in={isFreeSlotsLoading}>
                      <div>
                        <BasicLoader loaderProps={{ size: 30 }} />
                      </div>
                    </Collapse>
                  ) : null}
                  <Fade in={freeSlots.length > 0}>
                    <div>
                      <Grid container spacing={2}>
                        {freeSlots.map((slot, index) => {
                          const isSelected = Boolean(
                            state.selectedSlot &&
                              state.selectedSlot.start === slot.start
                          );

                          return (
                            <Grid
                              key={`slot-${index}`}
                              item
                              xs={6}
                              sm={4}
                              lg={3}
                            >
                              <SlotCard
                                slot={slot}
                                isSelected={isSelected}
                                onClick={() => {
                                  updateStateAttributes({ selectedSlot: slot });
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </div>
                  </Fade>
                  {!freeSlots.length && !isFreeSlotsLoading ? (
                    <div>
                      <Typography variant="body1">
                        No slots available! Please message your tutor in the
                        chat and ask them to add free slots so you can book a
                        class.
                      </Typography>
                    </div>
                  ) : null}
                </Stack>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          disabled={!state.selectedSlot || isBookingLoading}
          loading={isBookingLoading}
          variant="contained"
          color="inherit"
          sx={{ ...defaultButtonStyles }}
          onClick={handleSubmit}
        >
          Book Lesson
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BookClassModal;
